import { createContext, useContext } from 'react';
import type { BadgeProps } from '@buildbee/ui';
import { trpc, type RouterOutputs } from '../../lib/trpc';

export type ProjectDetail = RouterOutputs['projects']['get'];
export type Lookups = RouterOutputs['projects']['lookups'];

/** The project loaded by ProjectLayout, shared with every project tab. */
export const ProjectContext = createContext<ProjectDetail | null>(null);

export function useProject(): ProjectDetail {
  const p = useContext(ProjectContext);
  if (!p) throw new Error('useProject must be used inside a project route');
  return p;
}

export function useLookups(projectId: string) {
  return trpc.projects.lookups.useQuery({ projectId }, { staleTime: 60_000 });
}

type Variant = NonNullable<BadgeProps['variant']>;

const STATUS_VARIANTS: Record<string, Variant> = {
  // projects
  planning: 'outline',
  preconstruction: 'info',
  active: 'success',
  on_hold: 'warning',
  punch: 'primary',
  completed: 'default',
  closed: 'default',
  cancelled: 'destructive',
  // rfis / punch / generic
  draft: 'outline',
  open: 'info',
  answered: 'success',
  void: 'destructive',
  in_progress: 'primary',
  ready_for_review: 'warning',
  // submittals
  submitted: 'info',
  in_review: 'primary',
  approved: 'success',
  approved_as_noted: 'success',
  revise_resubmit: 'warning',
  rejected: 'destructive',
  // schedule
  not_started: 'outline',
  blocked: 'destructive',
  done: 'success',
  // bids
  leveling: 'primary',
  awarded: 'success',
  invited: 'outline',
  viewed: 'info',
  declined: 'default',
  not_awarded: 'default',
  withdrawn: 'default',
  // meetings
  scheduled: 'info',
  minutes_draft: 'warning',
  minutes_published: 'success',
  // health
  on_track: 'success',
  at_risk: 'warning',
  behind: 'destructive',
};

export function statusVariant(status: string): Variant {
  return STATUS_VARIANTS[status] ?? 'default';
}

export function statusLabel(status: string): string {
  const special: Record<string, string> = {
    approved_as_noted: 'Approved as noted',
    revise_resubmit: 'Revise & resubmit',
    ready_for_review: 'Ready for review',
    minutes_draft: 'Minutes draft',
    minutes_published: 'Minutes published',
    on_track: 'On track',
    at_risk: 'At risk',
    not_awarded: 'Not awarded',
    on_hold: 'On hold',
  };
  return special[status] ?? status.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

/** PUT a file to a signed storage URL returned by `projects.files.requestUpload`. */
export async function putToSignedUrl(uploadUrl: string, file: Blob, contentType: string): Promise<void> {
  const res = await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'content-type': contentType || 'application/octet-stream' }, credentials: 'include' });
  if (!res.ok) throw new Error(`Upload failed (${res.status})`);
}

export type UploadPurpose = 'document' | 'drawing' | 'photo' | 'attachment';

/** Two-step upload: ask the API for a signed URL, then send the bytes. Returns the storage key. */
export function useUploader(projectId: string) {
  const request = trpc.projects.files.requestUpload.useMutation();
  return {
    pending: request.isPending,
    async upload(file: File, purpose: UploadPurpose): Promise<{ key: string; name: string; contentType: string; sizeBytes: number }> {
      const contentType = file.type || 'application/octet-stream';
      const { key, uploadUrl } = await request.mutateAsync({ name: file.name, contentType, sizeBytes: Math.max(file.size, 1), purpose, projectId });
      await putToSignedUrl(uploadUrl, file, contentType);
      return { key, name: file.name, contentType, sizeBytes: file.size };
    },
  };
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}

/** Local calendar date as YYYY-MM-DD. */
export function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Dollars typed by a user -> integer cents (null for blank/invalid). */
export function dollarsToCents(value: string): number | null {
  const n = Number(value.replace(/[$,\s]/g, ''));
  return value.trim() === '' || !Number.isFinite(n) ? null : Math.round(n * 100);
}

export function centsToDollars(cents: number | null | undefined): string {
  return cents == null ? '' : (cents / 100).toFixed(2);
}

/** Empty strings from form inputs become null for optional API fields. */
export function blankToNull<T extends Record<string, unknown>>(values: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(values)) out[k] = v === '' ? null : v;
  return out as T;
}
